/**
 * Sơ đồ kho Summary Bảo Lộc — bố cục bám Access `frmFactorySummarySubForm2`
 * (BL.A.* / BL.B.* / BL.C.*, Text952–956, Text1374/996/1380/1379/1000/999).
 */
import { Fragment, useMemo } from 'react'
import { Box, Paper, Typography } from '@mui/material'

import type { FactoryCell, FactorySummaryWarehouseSplit } from '@/store/api/factoryApi'
import { PANE_8_2, WIDE_SCROLL_WRAPPER } from '@/theme/responsive'
import { BlockTitle } from '../maps/BlockTitle'

export type FactorySummaryMapCell = { field: string; label: string; value: FactoryCell }

function normalizeStockKey(key: string): string {
  return key.trim().toUpperCase().replace(/\s+/g, '')
}

/** Lookup tồn theo full_area_code (BL.A.E1, …) từ API hoặc sumRow. */
export function buildFactoryBlMapStockLookup(cells: FactorySummaryMapCell[]): Map<string, FactoryCell> {
  const m = new Map<string, FactoryCell>()
  for (const c of cells) {
    const candidates = new Set<string>()
    const field = String(c.field ?? '').trim()
    const label = String(c.label ?? '').trim()
    if (field) {
      candidates.add(normalizeStockKey(field))
      const bl = field.match(/BL\.[ABC]\.[A-Z0-9._]+/i)
      if (bl) candidates.add(normalizeStockKey(bl[0]))
    }
    if (label) {
      candidates.add(normalizeStockKey(label))
      const coord = label.match(/^([A-E])(\d(?:_\d)?)$/i)
      if (coord) {
        candidates.add(normalizeStockKey(`BL.A.${coord[1]}${coord[2]}`))
      }
      const bl2 = label.match(/BL\.[ABC]\.[A-Z0-9._]+/i)
      if (bl2) candidates.add(normalizeStockKey(bl2[0]))
    }
    for (const k of candidates) {
      if (k && !m.has(k)) m.set(k, c.value)
    }
  }
  return m
}

function getRowKeyCaseInsensitive(row: Record<string, FactoryCell> | null | undefined, normKey: string): string | undefined {
  if (!row) return undefined
  const want = normKey.toLowerCase()
  return Object.keys(row).find((k) => k.toLowerCase() === want)
}

function stringifyCell(v: FactoryCell): string {
  if (v === null || v === undefined) return ''
  return String(v)
}

function formatSummaryMetricValue(v: FactoryCell): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('en-US') : '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function hasQty(v: FactoryCell): boolean {
  return v !== null && v !== undefined && v !== '' && !(typeof v === 'number' && !Number.isFinite(v))
}

/**
 * Access KHO A (`frmFactorySummarySubForm2`) — bố cục bất thường có ô gộp:
 * hàng E gồm 6 ô (E1,E2,E2_1,E3,E4,E5); D2/C2/B2/A2 gộp ngang 2 cột; khối
 * "KHU VỰC SẢN XUẤT" (WIP) gộp cột 4–6 × hàng D–C; lò đốt + phòng bụi nằm
 * cột ngoài cùng bên phải. `col`/`row` ánh xạ thẳng vào grid (cột 1 = nhãn hàng).
 */
/** Màu nhãn trục (hàng E/D/C/B/A + số cột) — đỏ như Access. */
const MAP_AXIS_COLOR = '#d32f2f'

/** Nhãn số cột 1–5 dưới đáy KHO A; cột "2" gộp cột grid 3–4 (E2/E2_1 tách đôi). */
const KHO_A_COL_LABELS: Array<{ label: string; col: number | string }> = [
  { label: '1', col: 2 },
  { label: '2', col: '3 / 5' },
  { label: '3', col: 5 },
  { label: '4', col: 6 },
  { label: '5', col: 7 },
]

const KHO_A_ROW_LABELS = ['E', 'D', 'C', 'B', 'A'] as const
const KHO_A_CELLS: Array<{ area: string; col: number | string; row: number }> = [
  { area: 'BL.A.E1', col: 2, row: 1 },
  { area: 'BL.A.E2', col: 3, row: 1 },
  { area: 'BL.A.E2_1', col: 4, row: 1 },
  { area: 'BL.A.E3', col: 5, row: 1 },
  { area: 'BL.A.E4', col: 6, row: 1 },
  { area: 'BL.A.E5', col: 7, row: 1 },
  { area: 'BL.A.D1', col: 2, row: 2 },
  { area: 'BL.A.D2', col: '3 / 5', row: 2 },
  { area: 'BL.A.C1', col: 2, row: 3 },
  { area: 'BL.A.C2', col: '3 / 5', row: 3 },
  { area: 'BL.A.B1', col: 2, row: 4 },
  { area: 'BL.A.B2', col: '3 / 5', row: 4 },
  { area: 'BL.A.B3', col: 5, row: 4 },
  { area: 'BL.A.B5', col: 7, row: 4 },
  { area: 'BL.A.A1', col: 2, row: 5 },
  { area: 'BL.A.A2', col: '3 / 5', row: 5 },
  { area: 'BL.A.A3', col: 5, row: 5 },
  { area: 'BL.A.A4', col: 6, row: 5 },
  { area: 'BL.A.A5', col: 7, row: 5 },
]

/** Access: BL.B.* — 3 hàng (C/B/A) × 3 cột (1/2/3). */
const KHO_B_ROWS: Array<{ rowLabel: string; areas: string[] }> = [
  { rowLabel: 'C', areas: ['BL.B.C1', 'BL.B.C2', 'BL.B.C3'] },
  { rowLabel: 'B', areas: ['BL.B.B1', 'BL.B.B2', 'BL.B.B3'] },
  { rowLabel: 'A', areas: ['BL.B.A1', 'BL.B.A2', 'BL.B.A3'] },
]

/** Access: BL.C.* — 2 hàng (B/A) × 5 cột (1..5). */
const KHO_C_ROWS: Array<{ rowLabel: string; areas: string[] }> = [
  { rowLabel: 'B', areas: ['BL.C.B1', 'BL.C.B2', 'BL.C.B3', 'BL.C.B4', 'BL.C.B5'] },
  { rowLabel: 'A', areas: ['BL.C.A1', 'BL.C.A2', 'BL.C.A3', 'BL.C.A4', 'BL.C.A5'] },
]

const LABEL_SX = 'Processing Area'
const LABEL_LD = 'Incinerator & compressor area'
const LABEL_PB = 'Dust room'

const sectionTitleSx = {
  display: 'block',
  mb: 0.5,
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: 0.04,
  color: 'text.secondary',
}

const cellBorderSx = {
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  p: 0.25,
  minHeight: 34,
  textAlign: 'center' as const,
}

const allocationRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 1,
  py: 0.4,
  px: 0.75,
  borderRadius: 0.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(0,0,0,0.02)',
}

function WarehouseMiniGrid({
  colHeaders,
  rows,
  qty,
  maxWidth,
}: {
  colHeaders: string[]
  rows: Array<{ rowLabel: string; areas: string[] }>
  qty: (area: string) => FactoryCell
  maxWidth?: number | string
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `24px repeat(${colHeaders.length}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows.length}, minmax(34px, auto)) 16px`,
        gap: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0.5,
        overflow: 'hidden',
        maxWidth: maxWidth ?? '100%',
      }}
    >
      {rows.map((row, ri) => (
        <Fragment key={row.rowLabel}>
          <Box
            sx={{
              ...cellBorderSx,
              borderLeft: 0,
              bgcolor: 'background.paper',
              color: MAP_AXIS_COLOR,
              fontSize: '0.66rem',
              fontWeight: 800,
              gridColumn: 1,
              gridRow: ri + 1,
            }}
          >
            {row.rowLabel}
          </Box>
          {row.areas.map((area, ci) => {
            const v = qty(area)
            const short = area.replace(/^BL\.[ABC]\./i, '')
            return (
              <Box
                key={area}
                sx={{
                  ...cellBorderSx,
                  gridColumn: ci + 2,
                  gridRow: ri + 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ lineHeight: 1.05, fontSize: '0.58rem', color: hasQty(v) ? 'text.primary' : 'text.disabled' }}
                >
                  {short}
                </Typography>
                {hasQty(v) ? (
                  <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.7rem' }}>
                    {formatSummaryMetricValue(v)}
                  </Typography>
                ) : null}
              </Box>
            )
          })}
        </Fragment>
      ))}
      {/* Nhãn số cột ở đáy (Access) */}
      <Box sx={{ ...cellBorderSx, border: 0, minHeight: 16, gridColumn: 1, gridRow: rows.length + 1 }} />
      {colHeaders.map((h, ci) => (
        <Box
          key={`ch-${h}`}
          sx={{
            ...cellBorderSx,
            border: 0,
            minHeight: 16,
            color: MAP_AXIS_COLOR,
            fontSize: '0.6rem',
            fontWeight: 800,
            gridColumn: ci + 2,
            gridRow: rows.length + 1,
          }}
        >
          {h}
        </Box>
      ))}
    </Box>
  )
}

/** Một ô khu vực trong sơ đồ KHO A — luôn hiện mã ô; chỉ hiện số khi có tồn. */
function AreaCell({
  area,
  qty,
  gridColumn,
  gridRow,
}: {
  area: string
  qty: (a: string) => FactoryCell
  gridColumn: number | string
  gridRow: number | string
}) {
  const v = qty(area)
  const show = hasQty(v)
  const short = area.replace(/^BL\.[ABC]\./i, '')
  return (
    <Box sx={{ ...cellBorderSx, gridColumn, gridRow }}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ lineHeight: 1.05, fontSize: '0.56rem', color: show ? 'text.primary' : 'text.disabled' }}
      >
        {short}
      </Typography>
      {show ? (
        <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.68rem' }}>
          {formatSummaryMetricValue(v)}
        </Typography>
      ) : null}
    </Box>
  )
}

function AllocationLine({
  label,
  value,
}: {
  label: string
  value: FactoryCell
}) {
  return (
    <Box sx={allocationRowSx}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={800}>
        {formatSummaryMetricValue(value)}
      </Typography>
    </Box>
  )
}

/**
 * `scope` controls which parts of the dashboard map render:
 *  - 'all'        (default) — KHO A grid + KHO B + KHO C + PHÂN BỐ TỒN KHO
 *  - 'kho-a'      — chỉ render khối KHO A
 *  - 'kho-bc'     — chỉ render KHO B + KHO C
 *  - 'allocation' — chỉ render PHÂN BỐ TỒN KHO
 *
 * Bám layout Access `frmFactorySummarySubForm2` khi đặt cạnh mỗi section.
 */
/** Numeric coercion: trả về `null` cho ô không có giá trị thực (Access blank). */
function asNumber(v: FactoryCell): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

export function FactoryBaoLocSummaryMap({
  mapCells,
  sumRow,
  kvSx,
  warehouseSplit,
  scope = 'all',
  title,
  subtitle,
}: {
  mapCells: FactorySummaryMapCell[]
  sumRow?: Record<string, FactoryCell> | null
  kvSx?: number | null
  warehouseSplit?: FactorySummaryWarehouseSplit | null
  scope?: 'all' | 'kho-a' | 'kho-bc' | 'allocation'
  title?: string
  subtitle?: string
}) {
  const showKhoA = scope === 'all' || scope === 'kho-a'
  const showKhoBC = scope === 'all' || scope === 'kho-bc'
  const showAllocation = scope === 'all' || scope === 'allocation'
  const showMapPanel = showKhoA || showKhoBC
  const lookup = useMemo(() => buildFactoryBlMapStockLookup(mapCells), [mapCells])
  const qty = (area: string): FactoryCell => lookup.get(normalizeStockKey(area)) ?? null
  const hasMapData =
    lookup.size > 0 ||
    (typeof kvSx === 'number' && Number.isFinite(kvSx) && kvSx !== 0) ||
    Boolean(sumRow && Object.keys(sumRow).length > 0)

  const wipZone = qty('BL.A.SX')
  const ldknZone = qty('BL.A.LDKN')
  const pbZone = qty('BL.A.PB')
  const sxDisplay: FactoryCell =
    wipZone !== null && wipZone !== undefined && wipZone !== ''
      ? wipZone
      : typeof kvSx === 'number' && Number.isFinite(kvSx)
        ? kvSx
        : null

  // Allocation rows ("PHÂN BỔ TỒN KHO") — số do API trả về (summaryWarehouseSplit,
  // chính là các ô Text996/1379/1381/999/1004 trong Access). KHÔNG tự cộng lại
  // client-side (sai lệch vùng B). Fallback gom từ mapCells khi API thiếu split.
  const allocationRows = useMemo(() => {
    if (warehouseSplit) {
      return [
        { labelVi: 'Total (stack + WIP)', value: warehouseSplit.totalStackAndKv },
        { labelVi: 'Warehouse A (BL.A.*) + WIP', value: warehouseSplit.zoneAWithKv },
        { labelVi: 'Warehouse B (BL.B.*)', value: warehouseSplit.zoneB },
        { labelVi: 'Warehouse C (BL.C.*)', value: warehouseSplit.zoneC },
        { labelVi: 'Unassigned area', value: warehouseSplit.noArea },
        { labelVi: 'Processing Area', value: warehouseSplit.kvSx },
      ]
    }
    let za = 0, zb = 0, zc = 0, noArea = 0
    for (const c of mapCells) {
      const n = asNumber(c.value) ?? 0
      const f = (c.field || '').toUpperCase()
      if (f.startsWith('BL.A.')) za += n
      else if (f.startsWith('BL.B.')) zb += n
      else if (f.startsWith('BL.C.')) zc += n
      else noArea += n
    }
    const kv = asNumber(kvSx ?? null) ?? 0
    const total = za + zb + zc + noArea + kv
    return [
      { labelVi: 'Total (stack + WIP)', value: total },
      { labelVi: 'Warehouse A (BL.A.*) + WIP', value: za + kv },
      { labelVi: 'Warehouse B (BL.B.*)', value: zb },
      { labelVi: 'Warehouse C (BL.C.*)', value: zc },
      { labelVi: 'Unassigned area', value: noArea },
      { labelVi: 'Processing Area', value: kv },
    ]
  }, [mapCells, kvSx, warehouseSplit])
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BlockTitle title={title ?? 'Factory map'} />

      <Box sx={{ ...PANE_8_2, gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' }, gap: 1.5, alignItems: 'start' }}>
      {showMapPanel && !hasMapData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            No warehouse map data
          </Typography>
        </Box>
      ) : showMapPanel ? (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {showKhoA && (
        <>
        <Typography component="span" sx={sectionTitleSx}>
          Warehouse A
        </Typography>
        <Box sx={{ ...WIDE_SCROLL_WRAPPER, mb: 1.25 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '24px repeat(7, minmax(0, 1fr))',
            gridTemplateRows: 'repeat(5, minmax(34px, auto)) 16px',
            gap: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 0.5,
            overflow: 'hidden',
            minWidth: { xs: 600, md: 'auto' },
          }}
        >
          {KHO_A_ROW_LABELS.map((rl, ri) => (
            <Box
              key={`rl-${rl}`}
              sx={{
                ...cellBorderSx,
                borderLeft: 0,
                bgcolor: 'background.paper',
                color: MAP_AXIS_COLOR,
                fontSize: '0.66rem',
                fontWeight: 800,
                gridColumn: 1,
                gridRow: ri + 1,
              }}
            >
              {rl}
            </Box>
          ))}

          {KHO_A_CELLS.map((c) => (
            <AreaCell key={c.area} area={c.area} qty={qty} gridColumn={c.col} gridRow={c.row} />
          ))}

          {/* Ô trống giữa B3–B5 (không có B4) — tô vàng nối khối WIP như Access */}
          <Box sx={{ ...cellBorderSx, gridColumn: 6, gridRow: 4, bgcolor: 'rgba(255, 235, 120, 0.55)' }} />

          {/* KHU VỰC SẢN XUẤT (WIP) — gộp cột 4–6 × hàng D–C */}
          <Box
            sx={{
              ...cellBorderSx,
              gridColumn: '5 / 8',
              gridRow: '2 / 4',
              bgcolor: 'rgba(255, 235, 120, 0.55)',
              p: 0.4,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.1, textAlign: 'center', fontSize: '0.58rem' }}>
              {LABEL_SX}
            </Typography>
            <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.72rem' }}>
              {formatSummaryMetricValue(sxDisplay)}
            </Typography>
          </Box>

          {/* Lò đốt & khí nén — cột ngoài cùng, hàng E–D–C */}
          <Box
            sx={{
              ...cellBorderSx,
              borderRight: 0,
              gridColumn: 8,
              gridRow: '1 / 4',
              bgcolor: 'rgba(255, 152, 65, 0.5)',
              px: 0.2,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.58rem', lineHeight: 1.1, textAlign: 'center' }}>
              {LABEL_LD}
            </Typography>
            {hasQty(ldknZone) ? (
              <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.72rem', lineHeight: 1.05 }}>
                {formatSummaryMetricValue(ldknZone)}
              </Typography>
            ) : null}
          </Box>

          {/* Phòng bụi — cột ngoài cùng, hàng B–A */}
          <Box
            sx={{
              ...cellBorderSx,
              borderRight: 0,
              gridColumn: 8,
              gridRow: '4 / 6',
              bgcolor: 'rgba(129, 199, 132, 0.5)',
              px: 0.2,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.58rem', lineHeight: 1.1, textAlign: 'center' }}>
              {LABEL_PB}
            </Typography>
            {hasQty(pbZone) ? (
              <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.72rem', lineHeight: 1.05 }}>
                {formatSummaryMetricValue(pbZone)}
              </Typography>
            ) : null}
          </Box>

          {/* Nhãn số cột 1–5 dưới đáy (Access) — cột 2 = "1", "2" gộp cột 3–4… */}
          {KHO_A_COL_LABELS.map((cl) => (
            <Box
              key={`cl-${cl.label}`}
              sx={{
                ...cellBorderSx,
                border: 0,
                minHeight: 16,
                color: MAP_AXIS_COLOR,
                fontSize: '0.6rem',
                fontWeight: 800,
                gridColumn: cl.col,
                gridRow: 6,
              }}
            >
              {cl.label}
            </Box>
          ))}
        </Box>
        </Box>
        </>
        )}

        {showKhoBC && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            alignItems: 'stretch',
          }}
        >
          <Box>
            <Typography component="span" sx={sectionTitleSx}>
              Warehouse B
            </Typography>
            <WarehouseMiniGrid colHeaders={['1', '2', '3']} rows={KHO_B_ROWS} qty={qty} />
          </Box>
          <Box>
            <Typography component="span" sx={sectionTitleSx}>
              Warehouse C
            </Typography>
            <WarehouseMiniGrid colHeaders={['1', '2', '3', '4', '5']} rows={KHO_C_ROWS} qty={qty} />
          </Box>
        </Box>
        )}
      </Box>
      ) : null}

      {showAllocation && (
      <Box
        sx={{
          minWidth: 0,
          pl: { xs: 0, md: 1 },
          borderLeft: { xs: 0, md: '1px dashed' },
          borderColor: 'divider',
        }}
      >
        <BlockTitle title="Stock allocation" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {allocationRows.map((r) => (
            <AllocationLine key={r.labelVi} label={r.labelVi} value={r.value} />
          ))}
        </Box>
      </Box>
      )}
      </Box>
    </Paper>
  )
}
